import { createContext } from 'react';

import type {
  createCDFView,
  createViewCache,
  createAPMAppDataService,
  createInFieldLocationConfigService,
  createTemplateItemsService,
  createUserPreferencesService,
} from '@cognite/apm-client';
import type { createFDMClient } from '@cognite/fdm-client';
import type { CogniteClient } from '@cognite/sdk';
import type { createTagService } from '@cognite/solution-tags';

import type { createCdmThreeDService } from '../../features/3d/cdm-3d-service';
import type { createActivityIDMMigrator } from '../../features/activities/activity-idm-migrator';
import type { createCdmActivityService } from '../../features/activities/cdm-activity-service';
import type { createOperationIDMMigrator } from '../../features/activities/operation-idm-migrator';
import type { AppConfigErrorScreen } from '../../features/app-config/app-config-error-screen/cdm-app-config-error-screen';
import type { createAssetIDMMigrator } from '../../features/asset/asset-idm-migrator';
import type { createCdmAssetService } from '../../features/asset/cdm-asset-service';
import type { createCdmEdgeToDirectRelationService } from '../../features/cdm-relationship-service/cdm-edge-to-direct-relation-service';
import type { createCdmChecklistService } from '../../features/checklist/cdm-checklist-service';
import type { createCdmChecklistSchedulerService } from '../../features/checklist/checklist-scheduler/cdm-checklist-scheduler-service';
import type { createActionsService } from '../../features/conditions/actions/actions-service';
import type { createConditionalActionsService } from '../../features/conditions/conditional-actions/conditional-actions-service';
import type { createConditionsService } from '../../features/conditions/conditions/conditions.service';
import type { createIdmMediaService } from '../../features/idm-media/services/idm-media-service';
import type { createIdmObservationsService } from '../../features/idm-observations/services/idm-observations-service';
import type { createIdmSearchService } from '../../features/idm-search/services/idm-search-service';
import type { createCdmMeasurementService } from '../../features/measurements/cdm-measurement-service';
import type { createCdmMediaService } from '../../features/media/cdm-media-service';
import type { createFileIDMMigrator } from '../../features/media/file-idm-migrator';
import type { createNotificationIDMMigrator } from '../../features/notifications/notification-idm-migrator';
import type { createNotificationService } from '../../features/notifications/notification-service';
import type { createCdmIntervalService } from '../../features/task/interval-list/cdm-interval-service';
import type { createCdmTemplateService } from '../../features/template/cdm-template-service';
import type { createCdmTimeseriesService } from '../../features/timeseries/cdm-timeseries-service';
import type { createTimeseriesIDMMigrator } from '../../features/timeseries/timeseries-idm-migrator';
import type { createViewService } from '../../features/view/services/view-service';
import type { useGetViewsByReferences } from '../../providers/hooks/cdm-use-get-views-by-references';
import type { useSaveCdmOfflineConfiguration } from '../offline/cdm-use-save-offline-configuration';
import type { useCdmSelectedLocationContext } from '../selected-location-provider/cdm-selected-location-context';

export interface ServiceFactories {
  createCDFView: typeof createCDFView;
  createViewCache: typeof createViewCache;
  createAPMAppDataService: typeof createAPMAppDataService;
  createInFieldLocationConfigService: typeof createInFieldLocationConfigService;
  createTemplateItemsService: typeof createTemplateItemsService;
  createUserPreferencesService: typeof createUserPreferencesService;
  createFDMClient: typeof createFDMClient;
  createTagService: typeof createTagService;
  createCdmThreeDService: typeof createCdmThreeDService;
  createCdmActivityService: typeof createCdmActivityService;
  createCdmAssetService: typeof createCdmAssetService;
  createCdmChecklistService: typeof createCdmChecklistService;
  createCdmChecklistSchedulerService: typeof createCdmChecklistSchedulerService;
  createActionsService: typeof createActionsService;
  createConditionalActionsService: typeof createConditionalActionsService;
  createConditionsService: typeof createConditionsService;
  createIdmMediaService: typeof createIdmMediaService;
  createIdmObservationsService: typeof createIdmObservationsService;
  createIdmSearchService: typeof createIdmSearchService;
  createViewService: typeof createViewService;
  createCdmMeasurementService: typeof createCdmMeasurementService;
  createCdmMediaService: typeof createCdmMediaService;
  createNotificationService: typeof createNotificationService;
  createCdmIntervalService: typeof createCdmIntervalService;
  createCdmTemplateService: typeof createCdmTemplateService;
  createCdmTimeseriesService: typeof createCdmTimeseriesService;
  createCdmEdgeToDirectRelationService: typeof createCdmEdgeToDirectRelationService;
}

export interface MigratorFactories {
  createActivityIDMMigrator: typeof createActivityIDMMigrator;
  createOperationIDMMigrator: typeof createOperationIDMMigrator;
  createAssetIDMMigrator: typeof createAssetIDMMigrator;
  createNotificationIDMMigrator: typeof createNotificationIDMMigrator;
  createTimeseriesIDMMigrator: typeof createTimeseriesIDMMigrator;
  createFileIDMMigrator: typeof createFileIDMMigrator;
}

export interface CdmFDMServicesProviderContextInterface {
  useCdmSelectedLocationContext: () => Pick<
    ReturnType<typeof useCdmSelectedLocationContext>,
    'selectedLocationConfig'
  >;
  useGetViewsByReferences: (
    ...args: Parameters<typeof useGetViewsByReferences>
  ) => Pick<ReturnType<typeof useGetViewsByReferences>, 'data' | 'isLoading'>;
  useSaveCdmOfflineConfiguration: typeof useSaveCdmOfflineConfiguration;
  useAuthContext: () => { client: CogniteClient };
  serviceFactories: ServiceFactories;
  migratorFactories: MigratorFactories;
  AppConfigErrorScreen: typeof AppConfigErrorScreen;
}

export const CdmFDMServicesProviderContext =
  createContext<CdmFDMServicesProviderContextInterface | null>(null);
